import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, CardContent, Typography, Switch, FormControlLabel, Box, Button, CircularProgress, Grid } from '@mui/material';
import { handleUnauthorized } from '../../TokenAuth/auth';

function ManageServiceToggles() {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toggles, setToggles] = useState({
        isCallEnabled: false,
        isChatEnabled: false,
        isAppointmentEnabled: false,
        isVideoCallEnabled: false
    });

    const token = localStorage.getItem("astroVani-admin-token");
    const baseUrl = process.env.REACT_APP_BASEURL;

    useEffect(() => {
        fetchToggles();
    }, []);

    const fetchToggles = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${baseUrl}/admin/manageServiceToggles`,
                {
                    headers: {
                        'Authorization': token,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.success) {
                const { isCallEnabled, isChatEnabled, isAppointmentEnabled, isVideoCallEnabled } = response.data.data || {};
                setToggles({
                    isCallEnabled: !!isCallEnabled,
                    isChatEnabled: !!isChatEnabled,
                    isAppointmentEnabled: !!isAppointmentEnabled,
                    isVideoCallEnabled: !!isVideoCallEnabled
                });
            } else {
                toast.error(response.data.message || 'Failed to fetch service toggles');
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
            } else {
                console.error('Error fetching toggles:', error);
                toast.error(error.response?.data?.message || 'Error fetching toggles');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleToggleChange = (event) => {
        const { name, checked } = event.target;
        setToggles(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const response = await axios.patch(
                `${baseUrl}/admin/manageServiceToggles`,
                toggles,
                {
                    headers: {
                        'Authorization': token,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.success) {
                toast.success(response.data.message || 'Service toggles updated successfully');
            } else {
                toast.error(response.data.message || 'Failed to update service toggles');
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleUnauthorized();
            } else {
                console.error('Error updating toggles:', error);
                toast.error(error.response?.data?.message || 'Error updating toggles');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom-0">
                <h4 className="mb-0 fw-bold">Manage Service Toggles</h4>
            </div>

            <div className="card-body">
                <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '12px' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                            Global Service Controls
                        </Typography>

                        <Grid container spacing={2}>
                            <Box className="col-md-3" sx={{ mt: 4, display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={toggles.isCallEnabled}
                                            onChange={handleToggleChange}
                                            name="isCallEnabled"
                                            color="primary"
                                        />
                                    }
                                    label="Enable Calls"
                                />
                            </Box>
                            <Box className="col-md-3" sx={{ mt: 4, display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={toggles.isChatEnabled}
                                            onChange={handleToggleChange}
                                            name="isChatEnabled"
                                            color="primary"
                                        />
                                    }
                                    label="Enable Chat"
                                />
                            </Box>
                            <Box className="col-md-3" sx={{ mt: 4, display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={toggles.isAppointmentEnabled}
                                            onChange={handleToggleChange}
                                            name="isAppointmentEnabled"
                                            color="primary"
                                        />
                                    }
                                    label="Enable Appointments"
                                />
                            </Box>
                            <Box className="col-md-3" sx={{ mt: 4, display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={toggles.isVideoCallEnabled}
                                            onChange={handleToggleChange}
                                            name="isVideoCallEnabled"
                                            color="primary"
                                        />
                                    }
                                    label="Enable Video Calls"
                                />
                            </Box>
                        </Grid>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}

export default ManageServiceToggles;
